import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, MessageCircle, ArrowUpRight, Loader2 } from "lucide-react";
import { MaskText, FadeUp } from "./MaskText";
import { useLocale } from "@/i18n/LocaleContext";
import { INSTAGRAM, WHATSAPP } from "@/i18n/translations";
import { smeContent } from "@/i18n/sme";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INTEREST_KEYS = ["website", "venture-investment", "digital-solutions", "something-else"];

export const Contact = () => {
  const { t, locale } = useLocale();
  const sme = smeContent[locale] || smeContent.en;
  const waHref = `${WHATSAPP.url}?text=${encodeURIComponent(sme.waPrefill)}`;
  const [form, setForm] = useState({ name: "", email: "", company: "", interest: t.contact.interests[0], message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t.contact.toastValidation);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.toastSuccess);
      setForm({ name: "", email: "", company: "", interest: t.contact.interests[0], message: "" });
    } catch (err) {
      toast.error(t.contact.toastError);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 py-3 text-nowi-text placeholder:text-white/30 focus:border-nowi-tech focus:outline-none transition-colors font-manrope";

  return (
    <section id="contact" className="relative border-t border-white/10 py-24 md:py-32" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-16">
          <FadeUp>
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-nowi-tech">{t.contact.kicker}</span>
          </FadeUp>
          <MaskText
            lines={t.contact.heading}
            className="mt-5 font-playfair text-4xl md:text-6xl leading-[0.95] tracking-[-0.02em]"
          />
        </div>

        <div className="grid lg:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {/* Form */}
          <div className="bg-nowi-bg p-8 md:p-12">
            <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-8">
                <input data-testid="contact-name" className={inputCls} placeholder={t.contact.name} value={form.name} onChange={update("name")} />
                <input data-testid="contact-email" type="email" className={inputCls} placeholder={t.contact.email} value={form.email} onChange={update("email")} />
              </div>
              <input data-testid="contact-company" className={inputCls} placeholder={t.contact.company} value={form.company} onChange={update("company")} />

              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nowi-muted">{t.contact.interestLabel}</label>
                <div className="mt-4 flex flex-wrap gap-2">
                  {t.contact.interests.map((it, i) => (
                    <button
                      type="button"
                      key={INTEREST_KEYS[i]}
                      data-testid={`interest-${INTEREST_KEYS[i]}`}
                      onClick={() => setForm({ ...form, interest: it })}
                      className={`font-mono text-[10px] uppercase tracking-[0.15em] px-4 py-2 border transition-colors ${
                        form.interest === it
                          ? "bg-nowi-text text-nowi-bg border-nowi-text"
                          : "border-white/15 text-nowi-muted hover:border-white/40"
                      }`}
                    >
                      {it}
                    </button>
                  ))}
                </div>
              </div>

              <textarea data-testid="contact-message" rows={4} className={`${inputCls} resize-none`} placeholder={t.contact.message} value={form.message} onChange={update("message")} />

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="inline-flex items-center gap-3 bg-nowi-text text-nowi-bg px-8 py-4 text-xs font-mono uppercase tracking-[0.15em] hover:bg-nowi-tech transition-colors disabled:opacity-60"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />}
                {loading ? t.contact.sending : t.contact.send}
              </button>
            </form>
          </div>

          {/* WhatsApp + details */}
          <div className="bg-nowi-surface p-8 md:p-12 flex flex-col justify-between">
            <div>
              <p className="font-playfair text-2xl md:text-3xl leading-snug text-nowi-text max-w-sm">{t.contact.igLine}</p>
            </div>

            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-chat-btn"
              className="group relative mt-10 flex items-center justify-between gap-4 border border-white/15 p-6 md:p-8 hover:border-[#25D366] transition-colors duration-300"
            >
              <div className="flex items-center gap-5">
                <MessageCircle className="w-9 h-9 text-[#25D366]" strokeWidth={1.25} />
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nowi-muted">{sme.ctaWhats}</div>
                  <div className="font-playfair text-2xl md:text-3xl">+46 728 300 501</div>
                </div>
              </div>
              <ArrowUpRight className="w-6 h-6 text-nowi-text group-hover:text-[#25D366] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" strokeWidth={1.5} />
            </a>

            <a
              href={INSTAGRAM.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ig-profile-link"
              className="mt-5 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-nowi-muted hover:text-nowi-venture transition-colors"
            >
              <Instagram className="w-4 h-4" strokeWidth={1.5} /> {INSTAGRAM.handle}
            </a>

            <div className="mt-10 grid grid-cols-2 gap-6 font-mono text-xs text-nowi-muted">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">{t.contact.emailLabel}</div>
                <a href="mailto:hello@nowi.digital" className="hover:text-nowi-text transition-colors" data-testid="contact-email-link">hello@nowi.digital</a>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">{t.contact.studioLabel}</div>
                {t.contact.studioValue}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
